import type { GroupTab } from '../../features/workbench/models/group-tab.model';

/** デフォルトAPI Base URL */
export const DEFAULT_API_BASE_URL =
  'https://api-stg.out.inventory-report.p-garage.jp/api/' as const;

/** 一覧取得エンドポイント */
export const LIST_SEARCH_ENDPOINT =
  'list/97dc2fcf-ca10-4947-8d05-fc486caeabbb/search' as const;

/** ダウンロードAPI Base URL */
export const DOWNLOAD_API_BASE_URL =
  'https://reportoutput-stg.out.inventory-report.p-garage.jp/api/' as const;

/** CSV ダウンロードエンドポイント POST */
export const CSV_DOWNLOAD_ENDPOINT =
  'Reports/97dc2fcf-ca10-4947-8d05-fc486caeabbb/OutputCsv?inventoryFilter=' as const;

/** PDF ダウンロードエンドポイント POST */
export const PDF_DOWNLOAD_ENDPOINT =
  'Reports/97dc2fcf-ca10-4947-8d05-fc486caeabbb/OutputInspectionListPdf?inventoryFilter=' as const;

/** ダウンロードアクションID */
export type DownloadActionId = 'csv' | 'pdf';

/** View ごとのダウンロード可否 */
export interface ViewDownloadAvailability {
  csv: boolean;
  pdf: boolean;
}

/** 固定Tab定義 */
export const DEFAULT_FIXED_TABS: readonly GroupTab[] = [
  {
    id: '__unregistered__',
    name: '未登録',
    type: 'default',
    order: 0,
    scopeFilterSelections: [],
    filterSelections: [],
    createdAt: null,
    updatedAt: null,
  },
  {
    id: '__create__',
    name: '+',
    type: 'create',
    order: 1,
    scopeFilterSelections: [],
    filterSelections: [],
    createdAt: null,
    updatedAt: null,
  },
  {
    id: '__all__',
    name: 'ALL',
    type: 'all',
    order: 2,
    scopeFilterSelections: [],
    filterSelections: [],
    createdAt: null,
    updatedAt: null,
  },
] as const;

/** デフォルトのアクティブTab ID */
export const DEFAULT_ACTIVE_TAB_ID = '__all__' as const;

/** デフォルト View CD */
export const DEFAULT_VIEW_CD = 1 as const;

/** デフォルト表示列キー */
export const DEFAULT_VISIBLE_COLUMN_KEYS: readonly string[] = [
  'sectionName',
  'productCd',
  'productSign',
  'productName',
  'distributionName',
  'shipNo',
  'targetType',
  'sizeName',
  'colorName',
  'status',
  'shipTargetDate',
  'shipTargetQuantity',
  'shipPlanningDate',
  'shipPlanningQuantity',
  'incomingTargetDate',
  'incomingTargetQuantity',
] as const;

/** 編集可能列キー */
export const EDITABLE_COLUMN_KEYS: readonly string[] = [
  'shipTargetDate',
  'shipTargetQuantity',
  'shipPlanningDate',
  'shipPlanningQuantity',
  'incomingTargetDate',
  'incomingTargetQuantity',
] as const;

/** View Cd = 1 デフォルト表示列キー */
export const VIEW_CD_1_VISIBLE_COLUMN_KEYS: readonly string[] = [
  'sectionName',
  'productCd',
  // 'skuCd',
  'productSign',
  'productName',
  'colorName',
  'sizeName',
  'firstDeliveryDate',
  'skuPlanQuantity',
  'distributionName',
  'shipNo',
  'distributionName',
  'targetType',
  // 'shipTargetDate',
  // 'shipTargetQuantity',
  // 'incomingTargetDate',
  // 'incomingTargetQuantity',
] as const;

/** View Cd = 1 編集可能列キー */
export const VIEW_CD_1_EDITABLE_COLUMN_KEYS: readonly string[] = [
  'shipTargetDate',
  'shipTargetQuantity',
  'incomingTargetDate',
  'incomingTargetQuantity',
] as const;

/** View Cd = 2 デフォルト表示列キー */
export const VIEW_CD_2_VISIBLE_COLUMN_KEYS: readonly string[] = [
  'productName',
  'productKana',
  'productSign',
  'sizeName',
  'colorName',
  'productionPlanQuantity',
  'finishedQuantity',
  'inspectionQuantityA',
  'inspectionQuantityB',
  'boxQuantity',
] as const;

/** View Cd = 2 編集可能列キー */
export const VIEW_CD_2_EDITABLE_COLUMN_KEYS: readonly string[] = [
  'finishedQuantity',
  'inspectionQuantityA',
  'inspectionQuantityB',
  'boxQuantity',
] as const;

/** View CD ごとのデフォルト表示列キーを取得 */
export function getDefaultVisibleColumnKeys(viewCd: number = DEFAULT_VIEW_CD): string[] {
  switch (viewCd) {
    case 1:
      return [...VIEW_CD_1_VISIBLE_COLUMN_KEYS];
    case 2:
      return [...VIEW_CD_2_VISIBLE_COLUMN_KEYS];
    default:
      return [...DEFAULT_VISIBLE_COLUMN_KEYS];
  }
}

/** View CD ごとのデフォルト編集可能列キーを取得 */
export function getDefaultEditableColumnKeys(viewCd: number = DEFAULT_VIEW_CD): string[] {
  switch (viewCd) {
    case 1:
      return [...VIEW_CD_1_EDITABLE_COLUMN_KEYS];
    case 2:
      return [...VIEW_CD_2_EDITABLE_COLUMN_KEYS];
    default:
      return [...EDITABLE_COLUMN_KEYS];
  }
}

/** View Cd = 1 デフォルト有効フィルターフィールドキー */
export const VIEW_CD_1_DEFAULT_ENABLED_FILTER_FIELD_KEYS: readonly string[] = [
  'sectionName',
  'productCd',
  'productName',
  'distributionName',
] as const;

/** View Cd = 1 フィルターフィールドキー候補 */
export const VIEW_CD_1_ENABLED_FILTER_FIELD_KEYS: readonly string[] = [
  'sectionName',
  'productCd',
  'productName',
  'distributionName',
  'targetType',
  'status',
  'sizeName',
  'colorName',
] as const;

/** View Cd = 2 デフォルト有効フィルターフィールドキー */
export const VIEW_CD_2_DEFAULT_ENABLED_FILTER_FIELD_KEYS: readonly string[] = [
  'productName',
  'productKana',
  'productSign',
] as const;

/** View Cd = 2 フィルターフィールドキー候補 */
export const VIEW_CD_2_ENABLED_FILTER_FIELD_KEYS: readonly string[] = [
  'productName',
  'productKana',
  'productSign',
  'sizeName',
  'colorName',
] as const;

/** View CD ごとのフィルターフィールド候補を取得 */
export function getAvailableFilterFieldKeys(viewCd: number = DEFAULT_VIEW_CD): string[] {
  switch (viewCd) {
    case 1:
      return [...VIEW_CD_1_ENABLED_FILTER_FIELD_KEYS];
    case 2:
      return [...VIEW_CD_2_ENABLED_FILTER_FIELD_KEYS];
    default:
      return [...Object.keys(FIELD_LABELS)];
  }
}

/** View CD ごとのデフォルト有効フィルターフィールドキーを取得 */
export function getDefaultEnabledFilterFieldKeys(viewCd: number = DEFAULT_VIEW_CD): string[] {
  switch (viewCd) {
    case 1:
      return [...VIEW_CD_1_DEFAULT_ENABLED_FILTER_FIELD_KEYS];
    case 2:
      return [...VIEW_CD_2_DEFAULT_ENABLED_FILTER_FIELD_KEYS];
    default:
      return [...getAvailableFilterFieldKeys(viewCd)];
  }
}

/** View CD ごとのダウンロード可否を取得 */
export function getViewDownloadAvailability(viewCd: number = DEFAULT_VIEW_CD): ViewDownloadAvailability {
  switch (viewCd) {
    case 1:
      return { csv: false, pdf: false };
    case 2:
      return { csv: true, pdf: true };
    default:
      return { csv: false, pdf: false };
  }
}

/** フィールド名マッピング（命名揺れ吸収用） */
export const FIELD_NAME_ALIASES: Readonly<Record<string, string>> = {
  shipPlannedDate: 'shipPlanningDate',
  shipPlannedQuantity: 'shipPlanningQuantity',
} as const;


/** フィールド表示ラベル */
export const FIELD_LABELS: Readonly<Record<string, string>> = {
  supplierCd: 'サプライヤーCD',
  sectionName: '部門',
  productCd: '品番CD',
  unitCd: '単品CD',
  skuCd: 'SKU CD',
  productName: '商品名',
  productSign: '商品記号',
  productKana: '品名カナ',
  sizeName: 'サイズ',
  colorName: 'カラー',
  productionPlanQuantity: '生産計画数',
  finishedQuantity: '上り数',
  inspectionQuantityA: '検品数A',
  inspectionQuantityB: '検品数B',
  boxQuantity: '箱数',
  status: 'ステータス',
  firstDeliveryDate: '初回納品日',
  skuPlanQuantity: '計画数',
  shipNo: '便(ロット)',
  distributionName: '振分先',
  shipTargetDate: '船積日',
  shipTargetQuantity: '船積数',
  shipPlanningDate: '入荷予定日',
  shipPlanningQuantity: '出荷予定数',
  targetType: '在庫対象区分',
  incomingTargetDate: '入荷実績日',
  incomingTargetQuantity: '入荷実績数',
} as const;

/** フィールドデータ型マッピング */
export const FIELD_DATA_TYPES: Readonly<Record<string, 'string' | 'number' | 'date'>> = {
  supplierCd: 'string',
  sectionName: 'string',
  productCd: 'string',
  unitCd: 'string',
  skuCd: 'string',
  productName: 'string',
  productSign: 'string',
  productKana: 'string',
  sizeName: 'string',
  colorName: 'string',
  productionPlanQuantity: 'number',
  finishedQuantity: 'number',
  inspectionQuantityA: 'number',
  inspectionQuantityB: 'number',
  boxQuantity: 'number',
  status: 'string',
  firstDeliveryDate: 'date',
  skuPlanQuantity: 'number',
  shipNo: 'string',
  distributionName: 'string',
  shipTargetDate: 'date',
  shipTargetQuantity: 'number',
  shipPlanningDate: 'date',
  shipPlanningQuantity: 'number',
  targetType: 'string',
  incomingTargetDate: 'date',
  incomingTargetQuantity: 'number',
} as const;
